import { format, isValid, parse } from "date-fns";

/** Read currentPharmacyPOS for display helpers without importing PharmacyProvider (avoids cycles). */
const readPharmacyPOSForFormats = () => {
  try {
    if (typeof localStorage === "undefined") return null;
    const saved = localStorage.getItem("currentPharmacyPOS");
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};
/** Display modes for the reusable TimePicker. */
export const TIME_FORMAT_12H = "12h";
export const TIME_FORMAT_24H = "24h";

/**
 * Canonical API / form-storage time format.
 * UI may show 12h or 24h; values emitted to forms/APIs must use this.
 */
export const API_TIME_FORMAT = "HH:mm";

/**
 * Fallback until the pharmacy time format is provided after login/API.
 * Matches Pharmacy Panel default (`12h`).
 */
export const DEFAULT_PHARMACY_TIME_FORMAT = TIME_FORMAT_12H;

/**
 * Normalize common pharmacy/UI time-format values to `12h` or `24h`.
 * Accepts 12 / 24, labels like "12 Hours", and pattern-like values.
 */
export const normalizeTimeFormat = (timeFormat) => {
  if (timeFormat == null || timeFormat === "") return DEFAULT_PHARMACY_TIME_FORMAT;

  // Numeric configs from future login/API: 12 | 24
  if (timeFormat === 12 || timeFormat === "12") return TIME_FORMAT_12H;
  if (timeFormat === 24 || timeFormat === "24") return TIME_FORMAT_24H;

  const raw = String(timeFormat).trim().toLowerCase().replace(/[_-]+/g, " ");

  if (
    raw === "12h" ||
    raw === "12hr" ||
    raw === "12 hrs" ||
    raw === "12 hour" ||
    raw === "12 hours" ||
    raw === "hh:mm a" ||
    raw === "hh:mm aa" ||
    raw === "h:mm a" ||
    raw === "h:mm aa" ||
    raw === "ampm" ||
    raw === "am/pm" ||
    raw.includes("12 hour")
  ) {
    return TIME_FORMAT_12H;
  }

  if (
    raw === "24h" ||
    raw === "24hr" ||
    raw === "24 hrs" ||
    raw === "24 hour" ||
    raw === "24 hours" ||
    raw === "hh:mm" ||
    raw.includes("24 hour")
  ) {
    return TIME_FORMAT_24H;
  }

  return DEFAULT_PHARMACY_TIME_FORMAT;
};

/**
 * Resolve the pharmacy's preferred display time format.
 * Reads common login/localStorage field names (e.g. pharmacyPOS.time_format).
 * Missing, empty, or unsupported values fall back to 12h.
 */
export const pickPharmacyTimeFormatRaw = (pharmacyPOS) => {
  if (!pharmacyPOS || typeof pharmacyPOS !== "object") return null;
  const candidates = [
    pharmacyPOS.time_format,
    pharmacyPOS.timeFormat,
    pharmacyPOS.currency?.time_format,
    pharmacyPOS.currency?.timeFormat,
    pharmacyPOS.preferred_time_format,
  ];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    if (String(candidate).trim() === "") continue;
    return candidate;
  }
  return null;
};

export const resolvePharmacyTimeFormat = (pharmacyPOS) => {
  const raw = pickPharmacyTimeFormatRaw(pharmacyPOS);
  if (raw == null) return DEFAULT_PHARMACY_TIME_FORMAT;
  return normalizeTimeFormat(raw);
};

/**
 * Normalize a login/API time_format into `12h` / `24h` for storage.
 * Empty/invalid → "".
 */
export const normalizeStoredPharmacyTimeFormat = (value) => {
  if (value == null || String(value).trim() === "") return "";
  const raw = String(value).trim().toLowerCase().replace(/[_-]+/g, " ");
  if (
    value === 12 ||
    value === "12" ||
    raw === "12h" ||
    raw === "12hr" ||
    raw === "12 hrs" ||
    raw === "12 hour" ||
    raw === "12 hours" ||
    raw === "hh:mm a" ||
    raw === "hh:mm aa" ||
    raw === "h:mm a" ||
    raw === "h:mm aa" ||
    raw === "ampm" ||
    raw === "am/pm" ||
    raw.includes("12 hour")
  ) {
    return TIME_FORMAT_12H;
  }
  if (
    value === 24 ||
    value === "24" ||
    raw === "24h" ||
    raw === "24hr" ||
    raw === "24 hrs" ||
    raw === "24 hour" ||
    raw === "24 hours" ||
    raw === "hh:mm" ||
    raw.includes("24 hour")
  ) {
    return TIME_FORMAT_24H;
  }
  return "";
};

/**
 * TimePicker display format priority:
 * Explicit prop → pharmacy stored format → existing default.
 * Empty/whitespace props are treated as missing.
 */
export const resolvePickerTimeFormat = (propFormat, pharmacyPOS) => {
  const hasExplicit =
    propFormat != null && String(propFormat).trim() !== "";
  if (hasExplicit) return normalizeTimeFormat(propFormat);
  return resolvePharmacyTimeFormat(pharmacyPOS);
};

export const is12HourTimeFormat = (timeFormat) =>
  normalizeTimeFormat(timeFormat) === TIME_FORMAT_12H;

/** date-fns pattern used for display helpers. */
export const getTimeDisplayPattern = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm aa" : "HH:mm";

/** moment/react-datetime timeFormat string for the existing picker UI. */
export const getMomentTimeFormat = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm A" : "HH:mm";

/** Input placeholder derived from the active time format. */
export const getTimeFormatPlaceholder = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? "hh:mm AM" : "HH:mm";

export const getTimeFormatMaxLength = (timeFormat) =>
  is12HourTimeFormat(timeFormat) ? 8 : 5;

/** Build a Date on today's calendar date from hours/minutes. */
export const buildTimeDate = (hours, minutes) => {
  const h = Number(hours);
  const m = Number(minutes);
  if (
    !Number.isInteger(h) ||
    !Number.isInteger(m) ||
    h < 0 ||
    h > 23 ||
    m < 0 ||
    m > 59
  ) {
    return null;
  }
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date;
};

/** Parse API/form value `HH:mm` into a Date. */
export const parseApiTimeValue = (value) => {
  if (value instanceof Date) return isValid(value) ? value : null;
  if (value == null || value === "") return null;

  const text = String(value).trim();
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(text);
  if (!match) return null;
  return buildTimeDate(match[1], match[2]);
};

/** Format a Date as API/form value `HH:mm`. */
export const formatApiTimeValue = (date) => {
  if (!(date instanceof Date) || !isValid(date)) return "";
  return format(date, API_TIME_FORMAT);
};

/** Format a Date for UI display using the pharmacy time format. */
export const formatDisplayTime = (date, timeFormat = DEFAULT_PHARMACY_TIME_FORMAT) => {
  if (!(date instanceof Date) || !isValid(date)) return "";
  return format(date, getTimeDisplayPattern(timeFormat));
};

/** Resolved pharmacy display time format from `currentPharmacyPOS` (fallback 12h). */
export const getPharmacyDisplayTimeFormat = () =>
  resolvePharmacyTimeFormat(readPharmacyPOSForFormats());

/**
 * Format any time-like value for UI using pharmacy `time_format` from currentPharmacyPOS.
 * Accepts API `HH:mm`, Date, or ISO datetime. Empty/invalid → `empty` (default "").
 * Does not change API/storage contracts (still HH:mm elsewhere).
 */
export const formatPharmacyTime = (value, empty = "") => {
  if (value == null || value === "") return empty;

  let date = null;
  if (value instanceof Date) {
    date = isValid(value) ? value : null;
  } else {
    const text = String(value).trim();
    if (!text) return empty;
    date = parseApiTimeValue(text);
    if (!date) {
      const asDate = new Date(text);
      date = isValid(asDate) ? asDate : null;
    }
  }

  if (!date) return empty;
  const formatted = formatDisplayTime(date, getPharmacyDisplayTimeFormat());
  return formatted || empty;
};

const clampHour24 = (hourText) => {
  if (hourText.length < 2) return hourText;
  const hour = Number(hourText);
  if (Number.isNaN(hour)) return hourText.slice(0, 2);
  return String(Math.min(Math.max(hour, 0), 23)).padStart(2, "0");
};

const clampHour12 = (hourText) => {
  if (hourText.length < 2) return hourText;
  const hour = Number(hourText);
  if (Number.isNaN(hour)) return hourText.slice(0, 2);
  if (hour === 0) return "12";
  return String(Math.min(Math.max(hour, 1), 12)).padStart(2, "0");
};

const clampMinute = (minuteText) => {
  if (minuteText.length < 2) return minuteText;
  const minute = Number(minuteText);
  if (Number.isNaN(minute)) return minuteText.slice(0, 2);
  return String(Math.min(Math.max(minute, 0), 59)).padStart(2, "0");
};

const extractMeridiemDraft = (raw) => {
  const letters = String(raw || "")
    .toUpperCase()
    .replace(/[^APM]/g, "");
  if (!letters) return "";
  if (letters.startsWith("PM") || letters === "P") {
    return letters.startsWith("PM") ? "PM" : "P";
  }
  if (letters.startsWith("AM") || letters === "A") {
    return letters.startsWith("AM") ? "AM" : "A";
  }
  return "";
};

/**
 * Guide typed/pasted input into the configured time format.
 * Supports Backspace/Delete without fighting the mask.
 */
export const maskTimeInput = (
  rawInput,
  timeFormat = DEFAULT_PHARMACY_TIME_FORMAT,
  previousMasked = ""
) => {
  const mode = normalizeTimeFormat(timeFormat);
  const previous = String(previousMasked || "");
  const incoming = String(rawInput || "");
  const isDeleting = incoming.length < previous.length;

  if (mode === TIME_FORMAT_24H) {
    const digits = incoming.replace(/\D/g, "").slice(0, 4);
    const hours = clampHour24(digits.slice(0, 2));
    const minutes = clampMinute(digits.slice(2, 4));

    if (minutes) return `${hours}:${minutes}`;
    if (hours.length === 2 && !isDeleting) return `${hours}:`;
    return hours;
  }

  const digits = incoming.replace(/\D/g, "").slice(0, 4);
  const hours = clampHour12(digits.slice(0, 2));
  const minutes = clampMinute(digits.slice(2, 4));
  const meridiem = extractMeridiemDraft(incoming);

  let result = hours;
  if (minutes) {
    result = `${hours}:${minutes}`;
  } else if (hours.length === 2 && !isDeleting) {
    result = `${hours}:`;
  }

  if (minutes.length === 2) {
    if (meridiem) {
      result = `${hours}:${minutes} ${meridiem}`;
    } else if (!isDeleting) {
      result = `${hours}:${minutes} `;
    } else {
      result = `${hours}:${minutes}`;
    }
  }

  return result;
};

/**
 * Parse typed display text into a Date.
 * Returns null for incomplete/invalid values.
 */
export const parseTypedTime = (text, timeFormat = DEFAULT_PHARMACY_TIME_FORMAT) => {
  if (text == null) return null;
  const raw = String(text).trim();
  if (!raw) return null;

  const mode = normalizeTimeFormat(timeFormat);

  if (mode === TIME_FORMAT_24H) {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(raw);
    if (!match) return null;
    return buildTimeDate(match[1], match[2]);
  }

  const match = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*(AM|PM)$/i.exec(raw);
  if (!match) return null;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3].toUpperCase();

  if (meridiem === "AM") {
    if (hours === 12) hours = 0;
  } else if (hours !== 12) {
    hours += 12;
  }

  return buildTimeDate(hours, minutes);
};

/**
 * Convert a Date / API string / display string to canonical API `HH:mm`.
 */
export const toApiTimeString = (value, timeFormat = DEFAULT_PHARMACY_TIME_FORMAT) => {
  if (value instanceof Date) return formatApiTimeValue(value);
  if (value == null || value === "") return "";

  const asDate =
    parseApiTimeValue(value) || parseTypedTime(String(value), timeFormat);
  return asDate ? formatApiTimeValue(asDate) : "";
};

/** Parse controlled picker value preferring API `HH:mm`. */
export const parseTimeValue = (value, timeFormat = DEFAULT_PHARMACY_TIME_FORMAT) =>
  parseApiTimeValue(value) || parseTypedTime(value, timeFormat);

/** Convenience: parse with date-fns display pattern (used by picker internals). */
export const parseDisplayTime = (text, timeFormat = DEFAULT_PHARMACY_TIME_FORMAT) => {
  const pattern = getTimeDisplayPattern(timeFormat);
  const parsed = parse(String(text || "").trim(), pattern, new Date());
  return isValid(parsed) ? parsed : parseTypedTime(text, timeFormat);
};
